// src/pages/CreateFromDocument.jsx
// « Moment isee.art » d'ARTKIV : déposez une facture / certificat / bordereau →
// ARTKIV extrait (OCR) → PROPOSE une (ou plusieurs) fiche(s) → vous vérifiez et
// corrigez → « Créer l'œuvre ». Réutilise le moteur OCR existant
// (api.vision.artkiv.com) + le mapper canonique (mapOcrToArtworkPatch).
//
// Règle §12 « proposer, pas inventer » : rien n'est créé sans validation ; le
// document source est conservé et rattaché à l'œuvre créée.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { supabase } from "../lib/supabaseClient";
import { useAuth } from "@/context/AuthContext";
import { mapOcrToArtworkPatch } from "../utils/mapOcrToArtworkPatch";
import { getOrCreateUnclassifiedCollection } from "../lib/unclassifiedCollection";
import AiDisclosure from "@/components/AiDisclosure";
import { FileText, Image as ImageIcon, Sparkles, Loader2, ArrowLeft, Check, X, Plus } from "lucide-react";
import { VISION_API_URL } from "@/config/domains";

const OCR_API = `${VISION_API_URL}/analyze-document`;
const DOC_BUCKET = "artworks-documents";
const IMG_BUCKET = "artworks-images";

const COPY = {
  fr: {
    back: "Retour", title: "Créer une œuvre depuis un document",
    subtitle: "Déposez une facture, un certificat ou un bordereau. ARTKIV en extrait les informations et vous propose une fiche — rien n'est créé sans votre validation.",
    docLabel: "Document (facture, certificat, bordereau)", docHint: "PDF ou image",
    docTypeLabel: "Type de document",
    docTypes: { invoice: "Facture / bordereau / acte", certificate: "Certificat d'authenticité", other: "Autre" },
    photosLabel: "Photo(s) de l'œuvre (facultatif)",
    analyze: "Analyser le document", analyzing: "Analyse en cours…",
    noDoc: "Ajoutez d'abord un document.",
    reviewTitle: "Vérifiez avant de créer", reviewSub: "Corrigez si nécessaire. Ces informations proviennent de votre document.",
    detected: (n) => `${n} œuvre${n > 1 ? "s" : ""} détectée${n > 1 ? "s" : ""}`,
    source: "Source", include: "Créer cette œuvre",
    fields: { title: "Titre", artist: "Artiste", year: "Année", medium: "Technique / medium", dimensions: "Dimensions",
      acquisition_type: "Type d'acquisition", acquisition_date: "Date d'acquisition", acquisition_price: "Prix", currency: "Devise",
      auction_house: "Maison de vente", auction_lot_number: "N° de lot" },
    collectionLabel: "Ajouter à la collection",
    create: "Créer l'œuvre", createN: (n) => `Créer ${n} œuvre${n > 1 ? "s" : ""}`, creating: "Création…",
    nothingSelected: "Sélectionnez au moins une œuvre.",
    createdTitle: "Créé", createdBody: (n) => `${n} fiche${n > 1 ? "s" : ""} créée${n > 1 ? "s" : ""}. Le document est joint.`,
    seeCollection: "Voir ma collection", another: "Importer un autre document",
    noExtract: "Aucune information exploitable n'a été trouvée dans ce document. Vous pouvez tout de même créer une fiche vide et la compléter.",
    createEmpty: "Créer une fiche à compléter", ocrError: "L'analyse a échoué. Réessayez ou créez la fiche manuellement.",
    untitled: "Sans titre",
    multiCta: "Plusieurs fichiers (photos + factures) ? Importer et regrouper →",
  },
  en: {
    back: "Back", title: "Create an artwork from a document",
    subtitle: "Drop an invoice, certificate or auction slip. ARTKIV extracts the information and proposes a record — nothing is created without your validation.",
    docLabel: "Document (invoice, certificate, auction slip)", docHint: "PDF or image",
    docTypeLabel: "Document type",
    docTypes: { invoice: "Invoice / slip / deed", certificate: "Certificate of authenticity", other: "Other" },
    photosLabel: "Artwork photo(s) (optional)",
    analyze: "Analyse the document", analyzing: "Analysing…",
    noDoc: "Add a document first.",
    reviewTitle: "Check before creating", reviewSub: "Correct if needed. This information comes from your document.",
    detected: (n) => `${n} artwork${n > 1 ? "s" : ""} detected`,
    source: "Source", include: "Create this artwork",
    fields: { title: "Title", artist: "Artist", year: "Year", medium: "Medium / technique", dimensions: "Dimensions",
      acquisition_type: "Acquisition type", acquisition_date: "Acquisition date", acquisition_price: "Price", currency: "Currency",
      auction_house: "Auction house", auction_lot_number: "Lot no." },
    collectionLabel: "Add to collection",
    create: "Create the artwork", createN: (n) => `Create ${n} artwork${n > 1 ? "s" : ""}`, creating: "Creating…",
    nothingSelected: "Select at least one artwork.",
    createdTitle: "Created", createdBody: (n) => `${n} record${n > 1 ? "s" : ""} created. The document is attached.`,
    seeCollection: "View my collection", another: "Import another document",
    noExtract: "No usable information was found in this document. You can still create an empty record and complete it.",
    createEmpty: "Create a record to complete", ocrError: "Analysis failed. Try again or create the record manually.",
    untitled: "Untitled",
    multiCta: "Several files (photos + invoices)? Import and group →",
  },
  de: {
    back: "Zurück", title: "Werk aus einem Dokument erstellen",
    subtitle: "Laden Sie eine Rechnung, ein Zertifikat oder ein Auktionsprotokoll hoch. ARTKIV extrahiert die Informationen und schlägt eine Akte vor — nichts wird ohne Ihre Bestätigung erstellt.",
    docLabel: "Dokument (Rechnung, Zertifikat, Protokoll)", docHint: "PDF oder Bild",
    docTypeLabel: "Dokumenttyp",
    docTypes: { invoice: "Rechnung / Protokoll / Urkunde", certificate: "Echtheitszertifikat", other: "Andere" },
    photosLabel: "Foto(s) des Werks (optional)",
    analyze: "Dokument analysieren", analyzing: "Analyse läuft…",
    noDoc: "Fügen Sie zuerst ein Dokument hinzu.",
    reviewTitle: "Vor dem Erstellen prüfen", reviewSub: "Bei Bedarf korrigieren. Diese Informationen stammen aus Ihrem Dokument.",
    detected: (n) => `${n} Werk${n > 1 ? "e" : ""} erkannt`,
    source: "Quelle", include: "Dieses Werk erstellen",
    fields: { title: "Titel", artist: "Künstler", year: "Jahr", medium: "Technik / Medium", dimensions: "Maße",
      acquisition_type: "Erwerbsart", acquisition_date: "Erwerbsdatum", acquisition_price: "Preis", currency: "Währung",
      auction_house: "Auktionshaus", auction_lot_number: "Losnr." },
    collectionLabel: "Zur Sammlung hinzufügen",
    create: "Werk erstellen", createN: (n) => `${n} Werk${n > 1 ? "e" : ""} erstellen`, creating: "Erstellen…",
    nothingSelected: "Wählen Sie mindestens ein Werk.",
    createdTitle: "Erstellt", createdBody: (n) => `${n} Akte${n > 1 ? "n" : ""} erstellt. Das Dokument ist angehängt.`,
    seeCollection: "Meine Sammlung ansehen", another: "Weiteres Dokument importieren",
    noExtract: "In diesem Dokument wurden keine verwertbaren Informationen gefunden. Sie können dennoch eine leere Akte erstellen.",
    createEmpty: "Zu vervollständigende Akte erstellen", ocrError: "Analyse fehlgeschlagen. Erneut versuchen oder manuell erstellen.",
    untitled: "Ohne Titel",
    multiCta: "Mehrere Dateien (Fotos + Rechnungen)? Importieren und gruppieren →",
  },
};
const pick = (l) => COPY[String(l || "fr").slice(0, 2).toLowerCase()] || COPY.fr;

// Champs éditables présentés dans la proposition (valeurs "extracted-like").
const EDITABLE = ["title", "artist", "year", "medium", "dimensions", "acquisition_type", "acquisition_date", "acquisition_price", "currency", "auction_house", "auction_lot_number"];

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(",")[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// Construit les valeurs éditables initiales depuis extracted_data (+ œuvre détectée).
function toEditable(merged) {
  return {
    title: merged.title || "",
    artist: merged.artist || "",
    year: merged.year || "",
    medium: merged.medium || merged.medium_clean || merged.technique || "",
    dimensions: merged.dimensions || "",
    acquisition_type: merged.acquisition_type || merged.sale_type || "",
    acquisition_date: merged.acquisition_date || merged.date || merged.invoice_date || merged.auction_sale_date || "",
    acquisition_price: merged.acquisition_price || merged.price || merged.total_amount || merged.auction_hammer_price || "",
    currency: merged.currency || "",
    auction_house: merged.auction_house || "",
    auction_lot_number: merged.auction_lot_number || merged.lot_number || "",
  };
}

const inS = { width: "100%", padding: "9px 11px", border: "1px solid #e5e3df", borderRadius: 6, fontSize: 14, fontFamily: "system-ui, sans-serif", outline: "none" };

export default function CreateFromDocument() {
  const navigate = useNavigate();
  const { user } = useAuth() || {};
  const { i18n } = useTranslation();
  const tx = pick(i18n.resolvedLanguage || i18n.language);

  const [doc, setDoc] = useState(null);
  const [docType, setDocType] = useState("invoice");
  const [photos, setPhotos] = useState([]);
  const [analyzing, setAnalyzing] = useState(false);
  const [props, setProps] = useState(null);   // array of { include, fields } | null
  const [noExtract, setNoExtract] = useState(false);
  const [collections, setCollections] = useState([]);
  const [collectionId, setCollectionId] = useState("");
  const [creating, setCreating] = useState(false);
  const [doneN, setDoneN] = useState(0);
  const [doneCol, setDoneCol] = useState(null);

  useEffect(() => {
    if (!user?.id) return;
    supabase.from("collections").select("id, title").eq("client_id", user.id).order("created_at", { ascending: false })
      .then(({ data }) => { setCollections(data || []); if (data?.[0]) setCollectionId(data[0].id); });
  }, [user?.id]);

  const analyze = useCallback(async () => {
    if (!doc) { alert(tx.noDoc); return; }
    setAnalyzing(true); setNoExtract(false);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      const b64 = await fileToBase64(doc);
      const res = await fetch(OCR_API, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ file: b64, document_type: docType, client_id: user?.id || null }),
      });
      if (!res.ok) throw new Error(`OCR ${res.status}`);
      const json = await res.json();
      const extracted = json.extracted_data || {};
      const detected = Array.isArray(json.artworks) && json.artworks.length ? json.artworks : [null];
      const list = detected.map((aw) => ({ include: true, fields: toEditable({ ...extracted, ...(aw || {}) }) }));
      const anyValue = list.some((p) => Object.values(p.fields).some((v) => String(v || "").trim()));
      setProps(list);
      setNoExtract(!anyValue);
    } catch (e) {
      console.warn("[CreateFromDocument] analyze", e);
      alert(tx.ocrError);
      setProps([{ include: true, fields: toEditable({}) }]);
      setNoExtract(true);
    } finally {
      setAnalyzing(false);
    }
  }, [doc, docType, tx, user?.id]);

  const setField = (idx, key, val) =>
    setProps((prev) => prev.map((p, i) => (i === idx ? { ...p, fields: { ...p.fields, [key]: val } } : p)));
  const toggleInclude = (idx) =>
    setProps((prev) => prev.map((p, i) => (i === idx ? { ...p, include: !p.include } : p)));

  const selectedCount = useMemo(() => (props || []).filter((p) => p.include).length, [props]);

  const create = useCallback(async () => {
    if (!user?.id || !props) return;
    const chosen = props.filter((p) => p.include);
    if (!chosen.length) { alert(tx.nothingSelected); return; }
    setCreating(true);
    try {
      const colId = collectionId || (await getOrCreateUnclassifiedCollection(user.id));

      // Uploader le document source une seule fois, référencé par toutes les œuvres.
      let docRef = null;
      if (doc) {
        const safe = doc.name.replace(/[^\w.-]+/g, "_");
        const path = `${user.id}/${(window.crypto?.randomUUID?.() || Date.now())}-${safe}`;
        const { error: upErr } = await supabase.storage.from(DOC_BUCKET).upload(path, doc, { upsert: false });
        if (!upErr) docRef = { name: doc.name, type: docType, storagePath: path, bucket: DOC_BUCKET };
      }

      // Uploader les photos (rattachées à la 1re œuvre créée).
      const uploadedImages = [];
      for (const f of photos) {
        const ext = (f.name.split(".").pop() || "jpg");
        const path = `${user.id}/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
        const { error } = await supabase.storage.from(IMG_BUCKET).upload(path, f);
        if (!error) {
          const { data: pub } = supabase.storage.from(IMG_BUCKET).getPublicUrl(path);
          uploadedImages.push({ id: Date.now() + Math.random(), path: pub.publicUrl, url: pub.publicUrl, isPrimary: uploadedImages.length === 0 });
        }
      }

      const provSource = doc ? { source: doc.name, source_type: docType, status: "extracted", at: new Date().toISOString() } : null;
      for (let i = 0; i < chosen.length; i++) {
        const patch = mapOcrToArtworkPatch({ extracted: chosen[i].fields, artwork: {} });
        const provenance = {};
        if (provSource) {
          Object.entries(chosen[i].fields).forEach(([k, v]) => { if (String(v || "").trim()) provenance[k] = provSource; });
        }
        const payload = {
          ...patch,
          title: patch.title || chosen[i].fields.title || tx.untitled,
          client_id: user.id,
          collection_id: colId,
          documents: docRef ? JSON.stringify([docRef]) : null,
          images: i === 0 ? uploadedImages : [],
          field_provenance: Object.keys(provenance).length ? provenance : {},
        };
        const { error } = await supabase.from("artworks").insert([payload]);
        if (error) throw error;
      }

      setDoneN(chosen.length);
      setDoneCol(colId);
    } catch (e) {
      console.warn("[CreateFromDocument] create", e);
      alert(e?.message || "");
    } finally {
      setCreating(false);
    }
  }, [user?.id, props, collectionId, doc, docType, photos, tx]);

  // Écran de succès
  if (doneN > 0) {
    return (
      <div style={{ background: "#faf8f4", padding: "48px 20px", fontFamily: "system-ui, sans-serif" }}>
        <div style={{ maxWidth: 560, margin: "0 auto", background: "#fff", border: "1px solid #e5e3df", borderRadius: 10, padding: 32, textAlign: "center" }}>
          <div style={{ width: 48, height: 48, borderRadius: "50%", background: "#f1f6f1", color: "#2c5f2d", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 14px" }}><Check size={26} /></div>
          <h1 style={{ fontFamily: "Georgia, serif", fontSize: 24, fontWeight: 400, color: "#1a1a1a", margin: 0 }}>{tx.createdTitle}</h1>
          <p style={{ fontSize: 14, color: "#6b6b6b", lineHeight: 1.6, margin: "8px 0 20px" }}>{tx.createdBody(doneN)}</p>
          <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
            <button type="button" onClick={() => navigate(`/collections/${doneCol}`)} style={{ padding: "11px 18px", background: "#1a1a1a", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: "pointer" }}>{tx.seeCollection}</button>
            <button type="button" onClick={() => { setDoc(null); setPhotos([]); setProps(null); setDoneN(0); }} style={{ padding: "11px 18px", background: "#fff", color: "#1a1a1a", border: "1px solid #e5e3df", borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: "pointer" }}>{tx.another}</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: "#faf8f4", padding: "32px 20px", fontFamily: "system-ui, sans-serif" }}>
      <div style={{ maxWidth: 720, margin: "0 auto" }}>
        <button onClick={() => navigate(-1)} style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "transparent", border: "none", color: "#6b6b6b", fontSize: 13, cursor: "pointer", marginBottom: 16 }}><ArrowLeft size={16} /> {tx.back}</button>
        <h1 style={{ fontFamily: "Georgia, serif", fontSize: 28, fontWeight: 400, color: "#1a1a1a", margin: 0, letterSpacing: "-0.02em" }}>{tx.title}</h1>
        <p style={{ fontSize: 14.5, color: "#6b6b6b", lineHeight: 1.6, margin: "8px 0 24px", maxWidth: 620 }}>{tx.subtitle}</p>

        {!props ? (
          <div style={{ background: "#fff", border: "1px solid #e5e3df", borderRadius: 10, padding: 24, display: "grid", gap: 18 }}>
            <div>
              <label style={lbl}>{tx.docLabel}</label>
              <label style={{ display: "flex", alignItems: "center", gap: 10, padding: "14px 16px", border: "1.5px dashed #d6d0c4", borderRadius: 8, cursor: "pointer", background: "#fafaf8" }}>
                <FileText size={20} color="#8b7355" />
                <span style={{ fontSize: 14, color: doc ? "#1a1a1a" : "#9ca3af" }}>{doc ? doc.name : tx.docHint}</span>
                <input type="file" accept=".pdf,image/*" onChange={(e) => setDoc(e.target.files?.[0] || null)} style={{ display: "none" }} />
              </label>
            </div>
            <div>
              <label style={lbl}>{tx.docTypeLabel}</label>
              <select value={docType} onChange={(e) => setDocType(e.target.value)} style={inS}>
                {Object.entries(tx.docTypes).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div>
              <label style={lbl}>{tx.photosLabel}</label>
              <label style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "9px 14px", border: "1px solid #e5e3df", borderRadius: 8, cursor: "pointer", fontSize: 13, color: "#6b6b6b" }}>
                <ImageIcon size={16} /> {photos.length ? `${photos.length}` : "+"}
                <input type="file" accept="image/*" multiple onChange={(e) => setPhotos(Array.from(e.target.files || []))} style={{ display: "none" }} />
              </label>
            </div>
            <div>
              <button type="button" disabled={analyzing || !doc} onClick={analyze}
                style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "12px 20px", background: "#1a1a1a", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: (analyzing || !doc) ? "not-allowed" : "pointer", opacity: (analyzing || !doc) ? 0.55 : 1 }}>
                {analyzing ? <Loader2 size={16} className="spin" /> : <Sparkles size={16} />} {analyzing ? tx.analyzing : tx.analyze}
              </button>
            </div>
            <button type="button" onClick={() => navigate("/import-batch")} style={{ justifySelf: "start", background: "transparent", border: "none", color: "#8b7355", fontSize: 12.5, fontWeight: 600, cursor: "pointer", padding: 0 }}>{tx.multiCta}</button>
          </div>
        ) : (
          <div style={{ display: "grid", gap: 16 }}>
            <div style={{ background: "#fff", border: "1px solid #e5e3df", borderRadius: 10, padding: "16px 18px" }}>
              <div style={{ fontFamily: "Georgia, serif", fontSize: 18, color: "#1a1a1a" }}>{tx.reviewTitle}</div>
              <div style={{ fontSize: 13, color: "#6b6b6b", marginTop: 3 }}>{noExtract ? tx.noExtract : tx.reviewSub}</div>
              {!noExtract && props.length > 1 && <div style={{ fontSize: 12.5, color: "#8b7355", marginTop: 6, fontWeight: 600 }}>{tx.detected(props.length)}</div>}
              <AiDisclosure type="extracted" compact style={{ marginTop: 10 }} />
              {doc && <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 8 }}>{tx.source} : {doc.name}</div>}
            </div>

            {props.map((p, idx) => (
              <div key={idx} style={{ background: "#fff", border: `1px solid ${p.include ? "#c9a961" : "#e5e3df"}`, borderRadius: 10, padding: 18 }}>
                {props.length > 1 && (
                  <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12, cursor: "pointer" }}>
                    <input type="checkbox" checked={p.include} onChange={() => toggleInclude(idx)} style={{ width: 16, height: 16, accentColor: "#8b7355" }} />
                    <span style={{ fontSize: 13, fontWeight: 600, color: "#1a1a1a" }}>{tx.include}</span>
                  </label>
                )}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(220px, 100%), 1fr))", gap: 12 }}>
                  {EDITABLE.filter((k) => !["auction_house", "auction_lot_number"].includes(k) || p.fields[k]).map((k) => (
                    <div key={k}>
                      <label style={lbl}>{tx.fields[k]}</label>
                      <input value={p.fields[k] ?? ""} onChange={(e) => setField(idx, k, e.target.value)} style={inS} />
                    </div>
                  ))}
                </div>
              </div>
            ))}

            <div style={{ background: "#fff", border: "1px solid #e5e3df", borderRadius: 10, padding: 18 }}>
              <label style={lbl}>{tx.collectionLabel}</label>
              <select value={collectionId} onChange={(e) => setCollectionId(e.target.value)} style={inS}>
                {collections.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
              </select>
            </div>

            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <button type="button" disabled={creating || selectedCount === 0} onClick={create}
                style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "12px 22px", background: "#1a1a1a", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: (creating || selectedCount === 0) ? "not-allowed" : "pointer", opacity: (creating || selectedCount === 0) ? 0.55 : 1 }}>
                {creating ? <Loader2 size={16} className="spin" /> : <Plus size={16} />} {creating ? tx.creating : (noExtract ? tx.createEmpty : (props.length > 1 ? tx.createN(selectedCount) : tx.create))}
              </button>
              <button type="button" onClick={() => setProps(null)} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "12px 18px", background: "#fff", color: "#6b6b6b", border: "1px solid #e5e3df", borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: "pointer" }}><X size={15} /> {tx.back}</button>
            </div>
          </div>
        )}
      </div>
      <style>{`.spin{animation:spin 1s linear infinite}@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

const lbl = { display: "block", fontSize: 11, color: "#6b6b6b", marginBottom: 6, fontFamily: "system-ui, sans-serif", textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 700 };
